import PermIdentityIcon from '@mui/icons-material/PermIdentity'
import { Box, IconButton, Typography } from '@mui/material'
import { GridRenderCellParams, useGridApiContext } from '@mui/x-data-grid-premium'
import { FC, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { DensityStyleTypes } from 'types'

interface PrimaryContactCellProps {
  params: GridRenderCellParams
  densityStyles: DensityStyleTypes
}

const PrimaryContactCell: FC<PrimaryContactCellProps> = ({ params, densityStyles }) => {
  const navigate = useNavigate()
  const apiRef = useGridApiContext()
  const primaryContact = params?.row?.primaryContact ?? null
  // Parent org rows should not be editable
  const isParentOrg = params?.row?.isParentOrg === true

  const handleNavigate = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation() // Prevent edit mode from triggering
      if (!primaryContact || !primaryContact?.contacts_uuid) return
      navigate(`/contacts/${primaryContact?.contacts_uuid}/details`)
    },
    [primaryContact, navigate]
  )

  const handleStartEdit = useCallback(
    (event: React.MouseEvent) => {
      // Prevent the click from triggering row selection
      event.stopPropagation()
      // Don't allow editing parent org rows
      if (isParentOrg) return
      // Use requestAnimationFrame to ensure the grid state is ready
      requestAnimationFrame(() => {
        try {
          apiRef.current.startCellEditMode({ id: params.id, field: params.field })
        } catch (e) {
          console.warn('[PrimaryContactCell] Failed to start edit mode:', e)
        }
      })
    },
    [apiRef, params.id, params.field, isParentOrg]
  )

  //@ts-ignore
  if (params?.rowNode?.isAutoGenerated) return null

  return (
    <Box
      key={`${params?.row?.PK} - ${primaryContact?.contacts_uuid}`}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        padding: densityStyles.padding,
        gap: densityStyles.gap,
      }}
    >
      {/* Editable area - clicking here enters edit mode (except for parent orgs) */}
      <Box
        // onClick={handleStartEdit}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          cursor: isParentOrg ? 'default' : 'pointer',
          gap: 1,
        }}
      >
        {primaryContact?.contacts_uuid && primaryContact?.name ? (
          <Typography
            sx={{
              fontSize: densityStyles.fontSize,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              color: isParentOrg ? 'text.secondary' : 'text.primary',
            }}
          >
            {primaryContact?.name}
          </Typography>
        ) : !isParentOrg ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: densityStyles.fontSize, fontStyle: 'italic' }}
          >
            Select...
          </Typography>
        ) : null}
      </Box>

      {/* Navigation icon - pinned to the right, only shows if contact exists */}
      {primaryContact?.contacts_uuid && (
        <IconButton
          size="small"
          onClick={handleNavigate}
          title="View contact details"
          sx={{
            p: 0.25,
            flexShrink: 0,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <PermIdentityIcon
            sx={{
              color: 'rgb(255, 123, 187)',
              height: densityStyles.iconSize,
              width: densityStyles.iconSize,
            }}
          />
        </IconButton>
      )}

      {/* Empty state icon - no contact selected */}
      {!primaryContact?.contacts_uuid && (
        <PermIdentityIcon
          sx={{
            color: 'rgba(255, 123, 187, 0.3)',
            height: densityStyles.iconSize,
            width: densityStyles.iconSize,
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  )
}
export default PrimaryContactCell
